/** @format */

import { apiRoomAvailabilityReduced } from '@/api/apiRoomAvailabilityReduced';
import { TReducerBooking, selectorBookingCommercialConditions } from '@/ducks/booking/selectors';
import { useGTMListType } from '@/ducks/general/hooks/useGTMListType';
import { selectorCurrentPage, selectorGtmListType, selectorIsLoadingSearch } from '@/ducks/general/selectors';
import { selectorAvailabilityRoom } from '@/ducks/shared/selectors';
import {
  GTMCityAndNeighborhoodsApartmentCarousel,
  GTMmapInteractionCard,
  GTMproductClick,
} from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { utilityCalculateRoomURL, utilityGetRoomMetaTag } from '@/ducks/room/helpers';
import { useAppTranslation } from '@/i18n/declarations';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { mediaQuery } from '@/theme/mediaQuery';
import { utilityIsRoomAvailableFrom } from '@/utils/helpers/isRoomAvailableFrom';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { Link } from 'gatsby-plugin-intl';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SwiperCore, { Controller, Keyboard, Navigation, Pagination } from 'swiper';
import { Content } from './partials/Content';
import { ParsedContent } from './partials/ParsedContent';
import { Pictures } from './partials/Pictures';
import { StringParam, useQueryParam } from 'use-query-params';
import { NAME_PORTAL_CODE } from '@/utils/gatsby/constants';

SwiperCore.use([Pagination, Navigation, Controller, Keyboard]);

interface Props {
  room: TRoom;
  position?: number;
  hasInactiveURL?: boolean;
  isMapCard?: boolean;
  isMoreOptionCard?: boolean;
  isBookingCard?: boolean;
}

export const CardRoom = ({ room, hasInactiveURL, position, isMapCard, isMoreOptionCard, isBookingCard }: Props) => {
  const dispatch = useAppDispatch();
  const { t, intl } = useAppTranslation();
  const locale = intl.locale as 'it' | 'en';
  const { metaTitle } = utilityGetRoomMetaTag(locale, room.localizedData);
  const currentPage = useAppSelector(selectorCurrentPage);
  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);
  const roomURL = hasInactiveURL ? '' : utilityCalculateRoomURL(room);
  const gtmListType = useAppSelector(selectorGtmListType);
  const listType = isMoreOptionCard ? 'related_products' : gtmListType;
  const { onChangeGTMListType } = useGTMListType();
  const loadingListRoom = useAppSelector(selectorIsLoadingSearch);
  const initialRoomAvailability = useAppSelector(store => selectorAvailabilityRoom(store, room.code));
  const [availableFrom, setAvailableFrom] = useState(initialRoomAvailability?.availableFrom);
  const [roomAvailability, setRoomAvailability] = useState<TReducerBooking['availability']>(initialRoomAvailability);
  const [isLoading, setIsLoading] = useState(false);
  const controller = new AbortController();
  const [portalCode = NAME_PORTAL_CODE] = useQueryParam('portalCode', StringParam);

  useEffect(() => {
    if (!availableFrom && !loadingListRoom && !isBookingCard && !initialRoomAvailability) {
      setIsLoading(true);

      apiRoomAvailabilityReduced({ code: room.code, controller, portalCode: portalCode as string })
        .then(availability => {
          setIsLoading(false);
          if (availability) {
            setAvailableFrom(availability.availableFrom);
            setRoomAvailability(availability);
            dispatch({ type: 'SHARED_SET_AVAILABILITY_ARRAY', payload: availability });
          } else {
            setAvailableFrom('notAvailable');
            dispatch({
              type: 'SHARED_SET_AVAILABILITY_ARRAY',
              payload: { bedCode: room.code },
            });
          }
        })
        .catch(error => {
          setAvailableFrom('notAvailable');
          console.error(error);
        });
    }

    return () => {
      controller.abort();
    };
  }, [loadingListRoom]);

  useEffect(() => {
    if (roomAvailability && currentPage?.includes('request-')) {
      dispatch({
        type: 'GET_BOOKING_ROOM_AVAILABILITY_SUCCESS',
        payload: {
          price: roomAvailability.price,
          discountedPrice: roomAvailability.discountedPrice,
          discountUntil: roomAvailability.discountUntil,
          visibleOnline: roomAvailability.visibleOnline,
          availableFrom: roomAvailability.availableFrom,
          availableTo: roomAvailability.availableTo,
          bedAvailabilityId: roomAvailability.bedAvailabilityId,
          bedCode: roomAvailability.bedCode,
          checkInDates: roomAvailability.checkInDates,
          negotiating: roomAvailability.negotiating,
          status: roomAvailability.status,
          checkoutDateFrom: roomAvailability.checkoutDateFrom,
          checkoutDateTo: roomAvailability.checkoutDateTo,
          salableOnline: roomAvailability.salableOnline,
          validCheckoutDates: roomAvailability.validCheckoutDates,
          defaultCheckoutDate: roomAvailability.defaultCheckoutDate,
          evaluedExitWindows: roomAvailability.evaluedExitWindows,
        },
      });
    }
  }, [roomAvailability]);

  const { hasPromo, getDiscountedPrice, getFormattedPrice, getDiscountUntilDate, getDimension, getMetric } =
    utilityGetPrice({
      commercialConditions,
      availability: roomAvailability,
      room,
    });
  const formattedDiscountUntilDate = moment(getDiscountUntilDate()).format('DD/MM/YYYY');
  const isRoomAvailableFrom = utilityIsRoomAvailableFrom(roomAvailability);
  const isRoomNotAvailable = roomAvailability
    ? roomAvailability?.availableFrom === null || roomAvailability?.negotiating
    : true;

  const onClick = () => {
    isMoreOptionCard && onChangeGTMListType('related_products');

    GTMproductClick({
      actionField: { list: listType ?? 'other_list' },
      products: [
        {
          name: metaTitle,
          id: room.code,
          price: getFormattedPrice(),
          brand: `${room.city.name},${room.district.name}`,
          category: room.roomType,
          variant: '-',
          position: position + 1,
          dimension17: getDimension,
          metric1: getMetric,
          availability: isRoomNotAvailable
            ? 'not_available'
            : isRoomAvailableFrom
            ? 'available_on_next_weeks'
            : 'available',
        },
      ],
    });

    if (currentPage === 'city-page' || currentPage === 'neighborhood-page') {
      const gtmNameNeighborhood = `${room.city.name}_${room.district.name.split('/')[0].trimEnd()}`;
      const gtmName = currentPage === 'city-page' ? room.city.name : gtmNameNeighborhood.toLowerCase();
      GTMCityAndNeighborhoodsApartmentCarousel(
        gtmName,
        room.code,
        currentPage === 'city-page' ? 'city_page' : 'neighborhood_page',
      );
    }
    isMapCard && GTMmapInteractionCard(getFormattedPrice(), room.code);
  };

  return (
    <article style={{ padding: '5px 5px 40px' }}>
      <Outer $hasInactiveURL onClick={onClick}>
        {hasPromo() && (
          <Promo>
            <ParsedContent
              message={t('promo_banner', { price: getDiscountedPrice(), endPromo: formattedDiscountUntilDate })}
            />
          </Promo>
        )}
        <Pictures
          media={room.media}
          roomURL={roomURL}
          hasInactiveURL={hasInactiveURL ?? false}
          hasPromo={hasPromo()}
          room={room}
        />
        {hasInactiveURL ? (
          <Inner $hasInactiveURL>
            <Content
              locale={locale}
              hasInactiveURL
              room={room}
              availability={roomAvailability}
              availableFrom={availableFrom}
              isLoading={isLoading}
            />
          </Inner>
        ) : (
          <Link to={roomURL} style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer nofollow">
            <Inner>
              <Content
                locale={locale}
                room={room}
                availability={roomAvailability}
                availableFrom={availableFrom}
                isLoading={isLoading}
              />
            </Inner>
          </Link>
        )}
      </Outer>
    </article>
  );
};

// #region ::: COMPONENTS
const Outer = styled.div<{ $hasInactiveURL: boolean }>`
  background: ${({ theme }) => theme.colors.joivy.white};
  max-width: 445px;
  position: relative;
  height: 100%;
  min-height: ${({ $hasInactiveURL }) => ($hasInactiveURL ? 'auto' : '520px')};
  border-radius: 20px;
  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  :hover {
    ${({ $hasInactiveURL }) => !$hasInactiveURL && 'box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.325);'}
  }
`;
const Inner = styled.div<{ $hasInactiveURL?: boolean }>`
  padding: 20px 20px 30px 20px;
  height: ${({ $hasInactiveURL }) => ($hasInactiveURL ? 'auto' : '100%')};
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  ${mediaQuery.lg`
   height: ${({ $hasInactiveURL }: { $hasInactiveURL: boolean }) => ($hasInactiveURL ? 'auto' : '340px')};
  `}

  ${mediaQuery.md`
   height: ${({ $hasInactiveURL }: { $hasInactiveURL: boolean }) => ($hasInactiveURL ? 'auto' : '340px')};
  `}
`;

const Promo = styled.div`
  background-color: ${({ theme }) => theme.colors.joivy.pink};
  color: ${({ theme }) => theme.colors.joivy.white};
  margin: auto;
  padding: 10px;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  position: absolute;
  z-index: 2;
  width: 100%;
`;
// #endregion
